import { ErrorMessage } from '@hookform/error-message';
import { useFormContext } from 'react-hook-form';
import type {
  FieldErrors,
  RegisterOptions,
  UseFormReturn,
} from 'react-hook-form';
import { useRef, useEffect, useState, useContext } from 'react';
import { useTranslation } from '@/utils/i18n/useTranslation';

import FormError from '@/components/FormError/FormError';
import RichTextBlock from '@/components/RichTextBlock/RichTextBlock';
import { UserContext } from '@/context/UserContext';
import type {
  FormItemComponentProps,
  FormItemRequiredState,
} from '@/types/domain';

// The FormItem widgets read `errors` directly off the context; RHF actually exposes it on
// `formState.errors`, so it is `undefined` at runtime and only feeds the optional `errors`
// prop of `@hookform/error-message`. This alias models that (behaviour-preserving) shape.
type FormContextWithErrors = UseFormReturn & { errors?: FieldErrors };

const emptyClass = ' not-empty';
const revealClass = ' is-revealed';
const activeClass = ' is-active';
const requiredHint = ' *';

const FormItemTextfield = (props: FormItemComponentProps) => {
  const {
    item,
    onFormItemLoad,
    groupedState,
    revealState,
    shouldValidateState,
  } = props;

  const userContext = useContext(UserContext);
  /* eslint-disable @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove */
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT,
  );
  /* eslint-enable @typescript-eslint/no-unnecessary-condition */

  const { register, watch, errors, formState } =
    useFormContext() as unknown as FormContextWithErrors;
  const requiredErrorMessage = t('FORMITEM__ERROR_REQUIRED');

  const [isRequired, setIsRequired] = useState<FormItemRequiredState>({
    required: false,
  });
  const [isGrouped, setIsGrouped] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const itemRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
  const itemValue = watch(item.name!) as string;
  const itemProps = item;

  useEffect(() => {
    if (!item.revealed && item.required)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs form-item state from RHF/props, which is not render-derivable
      setIsRequired({ required: requiredErrorMessage });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, []);

  useEffect(() => {
    if (isLoaded || !itemValue) {
      return;
    }

    if (onFormItemLoad) onFormItemLoad({ itemProps, itemRef, itemValue });
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs form-item state from RHF/props, which is not render-derivable
    setIsLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, [itemValue]);

  useEffect(() => {
    if (item.revealed && item.required)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs form-item state from RHF/props, which is not render-derivable
      setIsRequired({ required: revealState ? requiredErrorMessage : false });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, [revealState]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs form-item state from RHF/props, which is not render-derivable
    if (!shouldValidateState) setIsRequired({ required: false });
  }, [shouldValidateState]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove
    if (!(item?.group && groupedState)) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove
    const itemLabel = item?.group?.label;
    const groupedItem = groupedState.find((str) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
      return str.label.includes(itemLabel!);
    });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, react-hooks/set-state-in-effect -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
    setIsGrouped(groupedItem!.active);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, [groupedState]);

  /* eslint-disable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove */
  return (
    <div
      ref={itemRef}
      className={`FormItem FormItem--${String(item.type)}${item.modifier ? ` FormItem--${item.modifier}` : ''}${
        itemValue ? emptyClass : ''
      }${revealState ? revealClass : ''}${isGrouped ? activeClass : ''}`}
      data-reveal-target={item.revealed}
    >
      <label
        className="FormItem-label"
        htmlFor={item.name}
      >{`${String(item.transl ? t(item.transl as string) : item.label)}${
        item.required ? requiredHint : ''
      }`}</label>
      <input
        type={item.inputtype || 'text'}
        id={item.name}
        defaultValue={`${item?.prefix ? t(item.prefix) : ''}${String(item.value || '')}`}
        placeholder={`${String(item.transl ? t(item.transl as string) : item.label)}${item.required ? requiredHint : ''}`}
        maxLength={item.maxLength}
        min={item.minDate}
        max={item.maxDate}
        disabled={item?.disabled}
        className={`FormItem-input FormItem-input--${String(item.type)}`}
        {...register(item.name!, isRequired as RegisterOptions)}
      />
      {formState.isSubmitted && (
        <ErrorMessage errors={errors} name={item.name!} as={<FormError />} />
      )}
      {typeof item.hint === 'string' && (
        <RichTextBlock
          content={item.transl ? t(item.hint) : item.hint}
          modifiers={['RichTextBlock--hint']}
        />
      )}
    </div>
  );
  /* eslint-enable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition */
};

export default FormItemTextfield;
